"use client";

import { useEffect, useId, useRef, type ReactNode } from "react";
import { useTranslations } from "use-intl";

/**
 * Body of the catalog "Perguntas frequentes" section: the heading plus one
 * two-column (lg) accordion list. Children are FAQ components built on
 * `CatalogFaqSection` (credits/companies FAQ, pricing FAQ), whose `<details>`
 * items flow straight into this grid. The first rendered item opens once on
 * mount; answers stay in the DOM whether open or not.
 */
export function CatalogFaqAccordion({
  children,
  titleId,
}: {
  children: ReactNode;
  /** Optional heading id so the page's section can use `aria-labelledby`. */
  titleId?: string;
}) {
  const t = useTranslations("release");
  const generatedId = useId();
  const headingId = titleId ?? generatedId;
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const first = listRef.current?.querySelector<HTMLDetailsElement>(
      "details[data-catalog-faq-item]",
    );
    if (first) first.open = true;
  }, []);

  return (
    <div className="min-w-0 has-[[data-catalog-faq-list]:empty]:hidden">
      <h2 id={headingId} className="mb-5 text-2xl font-bold tracking-tight sm:text-[28px]">
        {t("faqTitle")}
      </h2>
      <div
        ref={listRef}
        data-catalog-faq-list=""
        className="grid min-w-0 grid-cols-1 border-t border-border/60 lg:grid-cols-2 lg:gap-x-12"
      >
        {children}
      </div>
    </div>
  );
}
